import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Plus, Edit2, Trash2, Eye, EyeOff, Camera, Image, Video,
  Calendar, Loader2, AlertCircle, CheckCircle
} from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { MediaUploader } from '../../components/gallery'
import { Button, Modal, Input, Textarea, Select, Card, Badge } from '../../components/common'
import { formatDate } from '../../utils/helpers'

const categories = [
  { value: 'worship', label: 'Worship Services' },
  { value: 'youth', label: 'Youth Events' },
  { value: 'baptism', label: 'Baptisms' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'fellowship', label: 'Fellowship' },
  { value: 'special', label: 'Special Events' },
]

const GalleryForm = ({ gallery, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: gallery?.title || '',
    description: gallery?.description || '',
    date: gallery?.date || new Date().toISOString().split('T')[0],
    category: gallery?.category || 'worship',
    coverImage: gallery?.coverImage || '',
    media: gallery?.media || [],
    isPublished: gallery?.isPublished !== false,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      // Set cover image to first media if not set
      const coverImage = formData.coverImage ||
        formData.media.find(m => m.type === 'image')?.url ||
        ''

      await onSave({ ...formData, coverImage })
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleMediaUpdate = (media) => {
    setFormData({ ...formData, media })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Event Title"
          required
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Easter Sunday Celebration 2024"
        />
        <Input
          label="Event Date"
          type="date"
          required
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
        />
      </div>

      <Textarea
        label="Description"
        rows={3}
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        placeholder="A brief description of the event..."
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Select
          label="Category"
          required
          options={categories}
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
        />
        <Input
          label="Cover Image URL (optional)"
          type="url"
          value={formData.coverImage}
          onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
          placeholder="Will use first photo if empty"
        />
      </div>

      {/* Media Uploader */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Photos & Videos
        </label>
        <MediaUploader
          existingMedia={formData.media}
          onUpload={handleMediaUpdate}
        />
      </div>

      {/* Publish Toggle */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="isPublished"
          checked={formData.isPublished}
          onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
        />
        <label htmlFor="isPublished" className="text-sm text-gray-700">
          Publish this gallery (visible on public site)
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {gallery ? 'Update Gallery' : 'Create Gallery'}
        </Button>
      </div>
    </form>
  )
}

const GalleryManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editingGallery, setEditingGallery] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [actionMessage, setActionMessage] = useState(null)

  const { data: galleries, loading, add, update, remove } = useCollection('galleries', {
    orderByField: 'date',
    orderDirection: 'desc',
  })

  const showMessage = (message, isError = false) => {
    setActionMessage({ text: message, isError })
    setTimeout(() => setActionMessage(null), 3000)
  }

  const handleSave = async (data) => {
    try {
      if (editingGallery?.id) {
        await update(editingGallery.id, data)
        showMessage('Gallery updated successfully!')
      } else {
        await add(data)
        showMessage('Gallery created successfully!')
      }
      setShowModal(false)
      setEditingGallery(null)
    } catch (err) {
      throw err
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return

    try {
      await remove(deleteConfirm)
      showMessage('Gallery deleted successfully!')
      setDeleteConfirm(null)
    } catch (err) {
      showMessage('Failed to delete gallery', true)
      setDeleteConfirm(null)
    }
  }

  const togglePublish = async (gallery) => {
    try {
      await update(gallery.id, { isPublished: !gallery.isPublished })
      showMessage(`Gallery ${gallery.isPublished ? 'unpublished' : 'published'}!`)
    } catch (err) {
      showMessage('Failed to update gallery', true)
    }
  }

  const getMediaCounts = (gallery) => {
    const images = gallery.media?.filter(m => m.type === 'image').length || 0
    const videos = gallery.media?.filter(m => m.type === 'video').length || 0
    return { images, videos }
  }

  return (
    <div className="space-y-6">
      {/* Action Messages */}
      {actionMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
            actionMessage.isError
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
          }`}
        >
          {actionMessage.isError ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <CheckCircle className="w-5 h-5" />
          )}
          {actionMessage.text}
        </motion.div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Event Gallery</h1>
          <p className="text-gray-500">Manage photos and videos from church events</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditingGallery(null)
            setShowModal(true)
          }}
        >
          Add Gallery
        </Button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card animate={false} className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">{galleries?.length || 0}</p>
              <p className="text-sm text-gray-500">Total Galleries</p>
            </div>
          </div>
        </Card>
        <Card animate={false} className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
              <Image className="w-5 h-5 text-secondary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">
                {galleries?.reduce((acc, g) => acc + (g.media?.filter(m => m.type === 'image').length || 0), 0) || 0}
              </p>
              <p className="text-sm text-gray-500">Total Photos</p>
            </div>
          </div>
        </Card>
        <Card animate={false} className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-accent-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">
                {galleries?.reduce((acc, g) => acc + (g.media?.filter(m => m.type === 'video').length || 0), 0) || 0}
              </p>
              <p className="text-sm text-gray-500">Total Videos</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Galleries List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : galleries?.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleries.map((gallery) => {
            const { images, videos } = getMediaCounts(gallery)

            return (
              <Card key={gallery.id} animate={false} padding={false} className="overflow-hidden">
                {/* Cover Image */}
                <div className="relative h-48">
                  {gallery.coverImage || gallery.media?.[0]?.url ? (
                    <img
                      src={gallery.coverImage || gallery.media[0].url}
                      alt={gallery.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center">
                      <Camera className="w-12 h-12 text-primary-300" />
                    </div>
                  )}

                  {/* Status Badge */}
                  {gallery.isPublished === false && (
                    <div className="absolute top-2 left-2">
                      <Badge variant="gray">Draft</Badge>
                    </div>
                  )}

                  {/* Media Count */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {images > 0 && (
                      <span className="flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
                        <Image className="w-3 h-3" />
                        {images}
                      </span>
                    )}
                    {videos > 0 && (
                      <span className="flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
                        <Video className="w-3 h-3" />
                        {videos}
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-dark-100 mb-1 line-clamp-1">{gallery.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <Calendar className="w-4 h-4" />
                    {formatDate(gallery.date)}
                  </div>
                  <Badge variant="secondary" size="sm">{gallery.category}</Badge>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t">
                    <button
                      onClick={() => togglePublish(gallery)}
                      className={`p-2 rounded-lg transition-colors ${
                        gallery.isPublished !== false
                          ? 'text-emerald-600 hover:bg-emerald-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={gallery.isPublished !== false ? 'Unpublish' : 'Publish'}
                    >
                      {gallery.isPublished !== false ? (
                        <Eye className="w-5 h-5" />
                      ) : (
                        <EyeOff className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setEditingGallery(gallery)
                        setShowModal(true)
                      }}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                      title="Edit"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(gallery.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl">
          <Camera className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p className="text-gray-500 mb-4">No galleries yet</p>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowModal(true)}
          >
            Create Your First Gallery
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingGallery(null)
        }}
        title={editingGallery ? 'Edit Gallery' : 'Create Gallery'}
        size="xl"
      >
        <GalleryForm
          gallery={editingGallery}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingGallery(null)
          }}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Gallery"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this gallery? All photos and videos in this gallery will be removed. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default GalleryManager
